import React, { useState, useEffect, Fragment } from 'react'
import queryString from 'query-string'
import { useParams, useHistory, useLocation } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import { Button, Spinner, Typography } from '@telavita-core/react-design-kit'

import { Column } from 'components'
import NpsTextArea from 'components/NpsTextArea'
import Questionnaire from 'components/Questionnaire'

import { useIsPatientAllowedToSchedule } from 'hooks/useIsPatientAllowedToSchedule'
import { setSurveyAnswer } from 'redux/actions'

import ProfessionalsService from 'services/professionals'

import { SURVEY_CODE, answerType, questions, reviewsText } from 'settings/_surveyCodeSetting'
import { 
  DASHBOARD_AGENDA, 
  NPS_SCHEDULE_PAGE, 
  DASHBOARD_HOME_PATIENT, 
  NPS_REVIEW_COMPLETED_PAGE, 
  NPS_NEGATIVE_REVIEW
} from 'settings/_routesSettings'
import { TVTVPACI, TVTVPSIQ } from 'settings/_profileSettings'

const Survey = () => {
  const dispatch = useDispatch()

  const [loading, setLoading] = useState(true)
  const [isValid, setIsValid] = useState(false)
  const [isOpenReview, setIsOpenReview] = useState(false)
  const [answers, setAnswers] = useState([])
  const [textAreaFeedback, setTextAreaFeedback] = useState('')
  const [textAreaReview, setTextAreaReview] = useState('')
  const [next, setNext] = useState('')
  const [professionalProfileCode, setProfessionalProfileCode] = useState('')
  const [ratingReview, setRatingReview] = useState(null)

  const profileCode = useSelector(state => state.auth?.profileSelected?.profile_code || null)

  const { SCORE, TEXT } = answerType
  const { NEGATIVE, POSITIVE } = reviewsText

  const location = useLocation()
  const history = useHistory()
  const { scheduleId } = useParams()
  
  const { 
    isFirstSession, 
    isPatientAllowedToSchedule,
    isLoading,
    hasError
  } = useIsPatientAllowedToSchedule(scheduleId)

  useEffect(() => {
    const query = queryString.parse(location.search)

    setNext(query.next)
    setProfessionalProfileCode(query?.professional_profile_code)
    
    if (profileCode && profileCode !== TVTVPACI) {
      history.push(DASHBOARD_AGENDA)
    } else {
      setLoading(false)
    }
  }, []) // eslint-disable-line

  function singleResponseQuestion(value, questionCode) {
    const newAnswers = answers.filter(answer => answer.question_code !== questionCode)

    newAnswers.push({
      question_code: questionCode,
      schedule_id: Number(scheduleId),
      answer: value
    })

    setAnswers(newAnswers)
  }

  function handleSetReview(value) {
    const textArea = value.replace(/\n/g, '\n')
    setTextAreaReview(textArea)
  }

  function handleChangeQuestion(value, questionCode, type) {
    const isText = type === TEXT
    const inputValue = isText ? value.replace(/\n/g, '\n') : value
    
    if(isText) {
      setTextAreaFeedback(inputValue)
    }

    singleResponseQuestion(inputValue, questionCode)
  }

  function handleSubmit() {
    dispatch(setSurveyAnswer(SURVEY_CODE, { answers }))

    if (ratingReview) {
      ProfessionalsService.createReview({
        scheduleId: Number(scheduleId),
        rating: Number(ratingReview),
        review: textAreaReview,
      })
    }

    if (professionalProfileCode === TVTVPSIQ || hasError) {
      return history.push(NPS_REVIEW_COMPLETED_PAGE)
    }

    if (ratingReview !== null && ratingReview <= 6 && isFirstSession) {
      return history.push({
        pathname: NPS_NEGATIVE_REVIEW,
        state: {
          professionalProfileCode
        }
      })
    }

    if (ratingReview && isPatientAllowedToSchedule) {
      return history.push({
        pathname: NPS_SCHEDULE_PAGE,
        state: {
          next,
          showText: true,
        }
      })
    }

    return history.push(NPS_REVIEW_COMPLETED_PAGE)
  }

  function handleLeftToNextTime() {
    if (professionalProfileCode === TVTVPSIQ) {
      return history.push(DASHBOARD_HOME_PATIENT)
    }

    if (isPatientAllowedToSchedule) {
      return history.push({
        pathname: NPS_SCHEDULE_PAGE,
        state: {
          next,
          showText: false
        }
      })
    }

    history.push(DASHBOARD_HOME_PATIENT)
  }

  function isNegativeReviewValid() {
    if (ratingReview > 6 || ratingReview === null) {
      return true
    }

    const removedSpaceAndBreakLineFromReview = 
      textAreaReview.replace(/\s/g, '').replace(/\n/g, '')
    
    const MIN_REVIEW_LENGTH = 3
    return ratingReview <= 6 && removedSpaceAndBreakLineFromReview.length >= MIN_REVIEW_LENGTH
  }

  return loading
    ? (
      <div className='Survey__loading'>
        <Spinner />
      </div>
    )
    : (
      <div className='Survey' >
        <Column className='Survey__body'>

          <Typography
            center
            variant='header2'
          >
            Avalie sua experiência
          </Typography>
          <Typography
            customClassName='Survey__subheading'
            center
          >
            Com a sua ajuda, podemos melhorar cada vez mais!
          </Typography>

          {
            questions.map(question => {
              if (question.answerType === TEXT) {
                return (
                  <NpsTextArea
                    key={question.code}
                    name='suggestion'
                    questionText={question.text}
                    onChange={e => handleChangeQuestion(e.target.value, question.code, question.answerType)}
                    value={textAreaFeedback}
                  />
                )
              } else if(question.answerType === SCORE) {
                return (
                  <Questionnaire
                    key={question.code}
                    questionText={question.text}
                    questionCode={question.code}
                    handleClick={() => setIsValid(true)}
                    onSelectNumber={number => handleChangeQuestion(number, question.code, question.answerType)}
                  />
                )
              } else {
                return (
                  <Fragment key={question.code}>
                    <Questionnaire
                      questionText={question.text}
                      questionCode={question.code}
                      handleClick={() => {
                        setIsValid(true)
                        setIsOpenReview(true)
                      }}
                      onSelectNumber={number => {
                        handleChangeQuestion(number, question.code, SCORE)
                        setRatingReview(number)
                      }}
                    />
                    {
                      isOpenReview && (
                        <NpsTextArea
                          name='suggestion'
                          questionText='Deixe um depoimento sobre este profissional'
                          questionSubText={ratingReview > 8 ? POSITIVE : NEGATIVE}
                          onChange={e => handleSetReview(e.target.value)}
                          value={textAreaReview}
                          isRequired={ratingReview <= 6}
                        />
                      )
                    }
                  </Fragment>
                )
              }
            })
          }

          {
            !isValid && (
              <div className='Survey__message'>
                <Typography 
                  center
                  italic
                  color='danger'
                  variant='content2'
                >
                  Responda ao menos uma das questões acima.
                </Typography>
              </div>
            )
          }

          <div className='Survey__Button'>
            <Button
              disabled={!isValid || !isNegativeReviewValid() || isLoading}
              onClick={handleSubmit}
              variant='contained'
              uppercase={false}
            >
              Enviar opinião
            </Button>
            <Button
              customClassName='Survey__Button--secondary'
              disabled={isLoading}
              variant='contained'
              uppercase={false}
              onClick={handleLeftToNextTime}
            >
              Deixar para a próxima
            </Button>
          </div>
        </Column>
      </div>
    )
}

export default Survey